import React from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import useAuth from '../auth/feature/auth';
import { pricingService } from '../pricing/service/pricing.service';
import { HttpFactoryService } from '../common/services';
import { BACKEND_KEYS, ROUTER_LINKS } from '../common/consts/app-keys.const';
import { authService, IUserWebhook } from '../auth/service/auth.service';
import { UserAvatar, PRESET_AVATARS } from '../common/components/avatar.component';

const http = new HttpFactoryService().createHttpService();

export const SettingsPage = () => {
  const { user, setIsAuthenticated, checkAuth } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [activeSection, setActiveSection] = React.useState<string>('account');

  React.useEffect(() => {
    const section = searchParams.get('section');
    if (section) setActiveSection(section);
    if (searchParams.get('portal') === 'return') {
      const timer = setTimeout(() => checkAuth(), 2500);
      return () => clearTimeout(timer);
    }
  }, []);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [showCurrentPw, setShowCurrentPw] = React.useState(false);
  const [showNewPw, setShowNewPw] = React.useState(false);
  const [showConfirmPw, setShowConfirmPw] = React.useState(false);

  // Change password form
  const {
    register: regPw,
    handleSubmit: handlePw,
    reset: resetPw,
    formState: { errors: pwErrors }
  } = useForm<{ password: string; newPassword: string; confirmPassword: string }>();

  const { mutate: changePassword, isPending: pwPending } = useMutation({
    mutationFn: (data: { password: string; newPassword: string }) =>
      http.post(BACKEND_KEYS.CHANGE_PASSWORD, {
        email: user?.email,
        password: data.password,
        newPassword: data.newPassword
      }),
    onSuccess: () => {
      toast.success(t('settings.passwordUpdated'));
      resetPw();
    },
    onError: (err: any) => {
      const msg = err.response?.data?.message;
      toast.error(msg || t('settings.passwordChangeFailed'));
    }
  });

  // Manage subscription
  const { mutate: openPortal, isPending: portalPending } = useMutation({
    mutationFn: () => pricingService.getCustomerPortalUrl(),
    onSuccess: (data) => {
      if (data?.url) {
        window.location.href = data.url;
      }
    },
    onError: () => {
      toast.error(t('settings.portalFailed'));
    }
  });

  // Delete account
  const { mutate: deleteAccount, isPending: deletePending } = useMutation({
    mutationFn: () =>
      http.delete(BACKEND_KEYS.DELETE_ACCOUNT),
    onSuccess: async () => {
      toast.success(t('settings.accountDeleted'));
      await authService.logout();
      setIsAuthenticated(false);
      navigate(ROUTER_LINKS.ROOT);
    },
    onError: () => {
      toast.error(t('settings.accountDeleteFailed'));
    }
  });

  // Avatar
  const { mutate: updateAvatar } = useMutation({
    mutationFn: (avatar: string | null) =>
      http.post('user/update-avatar', { avatar }),
    onSuccess: () => {
      checkAuth();
      toast.success(t('settings.avatarUpdated'));
    },
    onError: () => toast.error(t('settings.avatarFailed'))
  });

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500_000) {
      toast.error(t('settings.imageTooLarge'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      updateAvatar(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const onPasswordSubmit = (data: {
    password: string;
    newPassword: string;
    confirmPassword: string;
  }) => {
    if (data.newPassword !== data.confirmPassword) {
      toast.error(t('settings.passwordsNoMatch'));
      return;
    }
    changePassword({ password: data.password, newPassword: data.newPassword });
  };

  const navItems = [
    { key: 'account', label: t('settings.account'), icon: accountIcon },
    // TODO: Add avatar picker
    // { key: 'avatar', label: 'Avatar', icon: avatarIcon },
    { key: 'password', label: t('settings.password'), icon: lockIcon },
    { key: 'security', label: t('settings.security'), icon: shieldIcon },
    { key: 'subscription', label: 'Subscription', icon: cardIcon },
    { key: 'webhooks', label: t('settings.webhooks'), icon: webhookIcon },
    { key: 'developer', label: 'Developer', icon: developerIcon },
    { key: 'danger', label: t('settings.deleteAccount'), icon: trashIcon }
  ];

  return (
    <div className="max-w-[700px] mx-auto py-[40px] px-[16px]">
      <Link to="/dashboard" className="inline-flex items-center gap-[4px] font-body text-[13px] font-medium text-[#818185] hover:text-base-black dark:hover:text-white transition-colors mb-[8px]">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
        {t('header.dashboard')}
      </Link>
      <h1 className="font-display text-[28px] font-bold text-base-black dark:text-white mb-[32px]">
        {t('settings.title')}
      </h1>

      <div className="bg-white dark:bg-[#1C1C1F] rounded-[16px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] shadow-[0_1px_3px_rgba(0,0,0,0.07),0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
        {/* Sidebar + Content layout */}
        <div className="flex flex-col md:flex-row h-[460px]">
          {/* Nav sidebar */}
          <nav className="md:w-[200px] flex-shrink-0 border-b md:border-b-0 md:border-r border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] flex md:flex-col">
            {navItems.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() =>
                  setActiveSection(item.key)
                }
                className={`flex items-center gap-[10px] px-[20px] py-[14px] font-body text-[14px] font-medium transition-all text-left w-full
                  ${
                    activeSection === item.key
                      ? 'bg-[#F0F0F2] dark:bg-[#28282B] text-base-black dark:text-white'
                      : 'text-[#818185] hover:bg-[#F7F7F8] dark:hover:bg-[#28282B] hover:text-base-black dark:hover:text-white'
                  }
                  ${item.key === 'danger' ? 'md:mt-auto' : ''}
                `}
              >
                <span className="w-[18px] h-[18px] flex items-center justify-center flex-shrink-0">
                  {item.icon}
                </span>
                <span className="hidden md:inline">{item.label}</span>
              </button>
            ))}
          </nav>

          {/* Content */}
          <div className="flex-1 p-[24px] overflow-y-auto">
            {!activeSection && (
              <div className="flex flex-col items-center justify-center h-full text-center py-[40px]">
                <p className="font-body text-[16px] text-gray-900 mb-[4px]">
                  {user?.email}
                </p>
                <p className="font-body text-[13px] text-[#818185]">
                  {t('settings.selectSection')}
                </p>
              </div>
            )}

            {activeSection === 'account' && (
              <div>
                <h2 className="font-body text-[18px] font-semibold text-base-black dark:text-white mb-[16px]">
                  {t('settings.account')}
                </h2>
                <div className="flex items-center gap-[12px] p-[16px] rounded-[12px] bg-[#F7F7F8] dark:bg-[#28282B] border border-[rgba(0,0,0,0.07)] dark:border-[rgba(255,255,255,0.06)] mb-[16px]">
                  <UserAvatar avatar={user?.avatar} name={user?.displayName || user?.email || ''} size={40} />
                  <div className="min-w-0">
                    <p className="font-body text-[15px] font-semibold text-base-black truncate">
                      {user?.displayName || user?.email?.split('@')[0]}
                    </p>
                    <p className="font-body text-[13px] text-[#818185] truncate">
                      {user?.email}
                    </p>
                  </div>
                </div>
                <DisplayNameEditor currentName={user?.displayName || ''} email={user?.email || ''} />
                <div className="mt-[12px]">
                  <EmailEditor currentEmail={user?.email || ''} />
                </div>
              </div>
            )}

            {activeSection === 'password' && (
              <div>
                <h2 className="font-body text-[18px] font-semibold text-base-black dark:text-white mb-[16px]">
                  {t('settings.changePassword')}
                </h2>
                <form
                  onSubmit={handlePw(onPasswordSubmit)}
                  className="flex flex-col gap-[12px] max-w-[360px]"
                >
                  <div>
                    <label className="font-body text-[13px] font-medium text-base-black dark:text-white mb-[4px] block ml-[2px]">
                      {t('settings.currentPassword')}
                    </label>
                    <div className="relative">
                      <input
                        type={showCurrentPw ? 'text' : 'password'}
                        placeholder={t('settings.currentPasswordPlaceholder')}
                        {...regPw('password', {
                          required: t('settings.currentPasswordRequired')
                        })}
                        className="w-full h-[44px] px-[16px] pr-[44px] text-[14px] font-body text-base-black dark:text-white bg-white dark:bg-[#28282B] border border-gray-700 dark:border-[rgba(255,255,255,0.12)] rounded-[24px] placeholder-gray-900 dark:placeholder-[#6B6B6F] focus:outline-none focus:border-[#BFBFC0] dark:focus:border-[#5DE1FF]/40 focus:shadow-[0_0_0_4px_rgba(29,29,30,0.12)] dark:focus:shadow-[0_0_0_4px_rgba(93,225,255,0.12)] transition-all"
                      />
                      <EyeToggle show={showCurrentPw} onToggle={() => setShowCurrentPw(!showCurrentPw)} />
                    </div>
                    {pwErrors.password && (
                      <p className="text-red-500 text-[12px] font-body mt-[4px] ml-[6px]">
                        {pwErrors.password.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="font-body text-[13px] font-medium text-base-black dark:text-white mb-[4px] block ml-[2px]">
                      {t('settings.newPassword')}
                    </label>
                    <div className="relative">
                      <input
                        type={showNewPw ? 'text' : 'password'}
                        placeholder={t('settings.newPasswordPlaceholder')}
                        {...regPw('newPassword', {
                          required: t('settings.newPasswordRequired'),
                          minLength: {
                            value: 8,
                            message: t('settings.passwordMinLength')
                          }
                        })}
                        className="w-full h-[44px] px-[16px] pr-[44px] text-[14px] font-body text-base-black dark:text-white bg-white dark:bg-[#28282B] border border-gray-700 dark:border-[rgba(255,255,255,0.12)] rounded-[24px] placeholder-gray-900 dark:placeholder-[#6B6B6F] focus:outline-none focus:border-[#BFBFC0] dark:focus:border-[#5DE1FF]/40 focus:shadow-[0_0_0_4px_rgba(29,29,30,0.12)] dark:focus:shadow-[0_0_0_4px_rgba(93,225,255,0.12)] transition-all"
                      />
                      <EyeToggle show={showNewPw} onToggle={() => setShowNewPw(!showNewPw)} />
                    </div>
                    {pwErrors.newPassword && (
                      <p className="text-red-500 text-[12px] font-body mt-[4px] ml-[6px]">
                        {pwErrors.newPassword.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="font-body text-[13px] font-medium text-base-black dark:text-white mb-[4px] block ml-[2px]">
                      {t('settings.confirmPassword')}
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPw ? 'text' : 'password'}
                        placeholder={t('settings.confirmPasswordPlaceholder')}
                        {...regPw('confirmPassword', {
                          required: t('settings.confirmPasswordRequired')
                        })}
                        className="w-full h-[44px] px-[16px] pr-[44px] text-[14px] font-body text-base-black dark:text-white bg-white dark:bg-[#28282B] border border-gray-700 dark:border-[rgba(255,255,255,0.12)] rounded-[24px] placeholder-gray-900 dark:placeholder-[#6B6B6F] focus:outline-none focus:border-[#BFBFC0] dark:focus:border-[#5DE1FF]/40 focus:shadow-[0_0_0_4px_rgba(29,29,30,0.12)] dark:focus:shadow-[0_0_0_4px_rgba(93,225,255,0.12)] transition-all"
                      />
                      <EyeToggle show={showConfirmPw} onToggle={() => setShowConfirmPw(!showConfirmPw)} />
                    </div>
                    {pwErrors.confirmPassword && (
                      <p className="text-red-500 text-[12px] font-body mt-[4px] ml-[6px]">
                        {pwErrors.confirmPassword.message}
                      </p>
                    )}
                  </div>
                  <button
                    type="submit"
                    disabled={pwPending}
                    className={`h-[44px] rounded-[32px] font-body text-[14px] font-medium text-white transition-all mt-[4px] ${
                      pwPending
                        ? 'bg-gray-900 opacity-50'
                        : 'bg-base-black hover:bg-[#2E2E30]'
                    }`}
                  >
                    {pwPending ? t('settings.updating') : t('settings.updatePassword')}
                  </button>
                </form>
              </div>
            )}

            {activeSection === 'security' && (
              <SecuritySection />
            )}

            {activeSection === 'subscription' && (
              <div>
                <h2 className="font-body text-[18px] font-semibold text-base-black dark:text-white mb-[16px]">
                  {t('settings.subscription')}
                </h2>

                {/* Plan info card */}
                <div className="p-[16px] rounded-[12px] bg-[#F7F7F8] dark:bg-[#28282B] border border-[rgba(0,0,0,0.07)] dark:border-[rgba(255,255,255,0.06)] mb-[16px]">
                  <div className="flex items-center justify-between mb-[4px]">
                    <p className="font-body text-[13px] text-[#818185] dark:text-[#A0A0A3]">
                      {t('settings.currentPlan')}
                    </p>
                    {/* Badge */}
                    {user?.isProGifted ? (
                      <span className="text-[11px] font-semibold px-[8px] py-[2px] rounded-[5px] bg-[#FFFBEB] text-[#92400E]">⭐ Pro</span>
                    ) : user?.subscriptionTier === 'trial' ? (
                      <span className="text-[11px] font-semibold px-[8px] py-[2px] rounded-[5px] bg-[#F8FCFF] text-[#0ea5c9] border border-[#BCEBFB]">Trial</span>
                    ) : user?.hasActiveSubscription && user?.subscriptionCancelledAt ? (
                      <span className="text-[11px] font-semibold px-[8px] py-[2px] rounded-[5px] bg-[#FEF2F2] text-[#D8210C]">Cancelling</span>
                    ) : user?.hasActiveSubscription ? (
                      <span className="text-[11px] font-semibold px-[8px] py-[2px] rounded-[5px] bg-[#1D1D1E] text-white">Pro</span>
                    ) : (
                      <span className="text-[11px] font-semibold px-[8px] py-[2px] rounded-[5px] bg-[#EFEFF1] text-[#818185]">Free</span>
                    )}
                  </div>
                  <p className="font-body text-[18px] font-semibold text-base-black dark:text-white capitalize">
                    {user?.isProGifted ? 'Pro (Gifted)'
                      : user?.subscriptionTier === 'trial' ? 'Free Trial'
                      : user?.hasActiveSubscription ? `Pro · ${user?.subscriptionTier || 'Monthly'}`
                      : 'Free'}
                  </p>

                  {/* Dates */}
                  {user?.subscriptionTier === 'trial' && user?.subscriptionExpiresAt && (
                    <p className="font-body text-[13px] text-[#0ea5c9] mt-[4px]">
                      {(() => {
                        const days = Math.max(0, Math.round((new Date(user.subscriptionExpiresAt!).getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
                        return `${days} days remaining · Ends ${new Date(user.subscriptionExpiresAt!).toLocaleDateString()}`;
                      })()}
                    </p>
                  )}
                  {user?.hasActiveSubscription && user?.subscriptionCancelledAt && user?.subscriptionExpiresAt && (
                    <p className="font-body text-[13px] text-[#D8210C] mt-[4px]">
                      Your plan will downgrade to Free on {new Date(user.subscriptionExpiresAt).toLocaleDateString()}
                    </p>
                  )}
                  {user?.hasActiveSubscription && !user?.subscriptionCancelledAt && user?.subscriptionExpiresAt && user?.subscriptionTier !== 'trial' && (
                    <p className="font-body text-[13px] text-[#818185] mt-[4px]">
                      Renews {new Date(user.subscriptionExpiresAt).toLocaleDateString()}
                    </p>
                  )}
                </div>

                {/* Action buttons */}
                <div className="flex gap-[8px] flex-wrap">
                  {user?.hasActiveSubscription && !user?.isProGifted && (
                    <button
                      type="button"
                      onClick={() => openPortal()}
                      disabled={portalPending}
                      className={`h-[44px] px-[24px] rounded-[32px] font-body text-[14px] font-medium transition-all ${
                        portalPending ? 'bg-gray-900 text-white opacity-50' : 'bg-base-black text-white dark:bg-[#C8C8CB] dark:text-[#1C1C1F] hover:bg-[#2E2E30] dark:hover:bg-[#D8D8DB]'
                      }`}
                    >
                      {portalPending ? t('common.loading') : t('settings.manageSubscription')}
                    </button>
                  )}
                  {(!user?.hasActiveSubscription || user?.subscriptionTier === 'trial') && !user?.isProGifted && (
                    <button
                      type="button"
                      onClick={() => navigate(ROUTER_LINKS.PRICING)}
                      className="h-[44px] px-[24px] rounded-[32px] bg-base-black text-white dark:bg-[#C8C8CB] dark:text-[#1C1C1F] font-body text-[14px] font-medium hover:bg-[#2E2E30] dark:hover:bg-[#D8D8DB] transition-all"
                    >
                      Upgrade to Pro
                    </button>
                  )}
                </div>
              </div>
            )}

            {activeSection === 'webhooks' && (
              <SavedWebhooksSection />
            )}

            {activeSection === 'developer' && (
              <ApiKeysSection />
            )}

            {activeSection === 'danger' && (
              <div>
                <h2 className="font-body text-[18px] font-semibold text-red-500 mb-[8px]">
                  {t('settings.deleteAccount')}
                </h2>
                <p className="font-body text-[14px] text-gray-900 dark:text-[#A0A0A3] mb-[16px]">
                  {t('settings.deleteWarning')}
                </p>
                {!confirmDelete ? (
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(true)}
                    className="h-[44px] px-[24px] rounded-[32px] border border-red-500 text-red-500 font-body text-[14px] font-medium hover:bg-red-500 hover:text-white transition-all"
                  >
                    {t('settings.deleteMyAccount')}
                  </button>
                ) : (
                  <div className="p-[16px] rounded-[12px] border border-red-500 bg-[#FEF2F2]">
                    <p className="font-body text-[14px] font-semibold text-red-500 mb-[12px]">
                      {t('settings.deleteConfirm')}
                    </p>
                    <div className="flex gap-[8px]">
                      <button
                        type="button"
                        onClick={() => deleteAccount()}
                        disabled={deletePending}
                        className={`h-[40px] px-[20px] rounded-[32px] font-body text-[13px] font-medium text-white transition-all ${
                          deletePending
                            ? 'bg-gray-900 opacity-50'
                            : 'bg-red-500 hover:bg-red-600'
                        }`}
                      >
                        {deletePending
                          ? t('settings.deleting')
                          : t('settings.deleteConfirmButton')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(false)}
                        className="h-[40px] px-[20px] rounded-[32px] border border-gray-500 bg-white font-body text-[13px] font-medium text-base-black hover:bg-[#F5F5F6] transition-all"
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Inline SVG icons ---
const accountIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const lockIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const shieldIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const cardIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);

const DisplayNameEditor = ({ currentName, email }: { currentName: string; email: string }) => {
  const [editing, setEditing] = React.useState(false);
  const [name, setName] = React.useState(currentName);
  const [copiedName, setCopiedName] = React.useState(false);
  const { checkAuth } = useAuth();
  const { t } = useTranslation();

  const { mutate: save, isPending } = useMutation({
    mutationFn: (displayName: string) =>
      new HttpFactoryService().createHttpService().post('user/update-display-name', { displayName }),
    onSuccess: () => {
      checkAuth();
      setEditing(false);
      toast.success(t('settings.displayNameUpdated'));
    },
    onError: () => toast.error(t('settings.displayNameFailed'))
  });

  if (!editing) {
    const displayValue = currentName || email.split('@')[0];
    return (
      <div className="flex items-center gap-[8px]">
        <p className="font-body text-[13px] text-[#818185]">{t('settings.displayName')}</p>
        <button
          type="button"
          onClick={() => {
            navigator.clipboard.writeText(displayValue);
            setCopiedName(true);
            setTimeout(() => setCopiedName(false), 2000);
          }}
          className="inline-flex items-center gap-[5px] h-[36px] px-[14px] rounded-[32px] bg-[#EFEFF1] dark:bg-[#28282B] text-[#4A4A4C] dark:text-[#A0A0A3] font-body text-[13px] font-semibold hover:bg-[#E5E5ED] dark:hover:bg-[#333336] transition-all"
        >
          {copiedName ? t('common.copied') : displayValue}
        </button>
        <button
          type="button"
          onClick={() => { setName(displayValue); setEditing(true); }}
          className="text-[#818185] hover:text-base-black dark:hover:text-white transition-colors flex-shrink-0"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-[6px]">
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={t('settings.enterDisplayName')}
        className="flex-1 h-[36px] px-[12px] text-[13px] font-body bg-white border border-gray-700 rounded-[18px] focus:outline-none focus:border-[#BFBFC0] focus:shadow-[0_0_0_4px_rgba(29,29,30,0.12)] transition-all"
      />
      <button
        type="button"
        onClick={() => save(name)}
        disabled={isPending}
        className="h-[36px] px-[12px] rounded-[18px] bg-[#1D1D1E] text-white font-body text-[12px] font-medium hover:bg-[#2E2E30] transition-all"
      >
        {t('common.save')}
      </button>
      <button
        type="button"
        onClick={() => setEditing(false)}
        className="h-[36px] px-[12px] rounded-[18px] border border-gray-500 bg-gray-400 font-body text-[12px] font-medium text-base-black hover:bg-[#E0E0E3] transition-all"
      >
        {t('common.cancel')}
      </button>
    </div>
  );
};

const EyeToggle = ({ show, onToggle }: { show: boolean; onToggle: () => void }) => (
  <button
    type="button"
    onClick={onToggle}
    className="absolute right-[14px] top-1/2 -translate-y-1/2 text-[#818185] hover:text-base-black transition-colors"
  >
    {show ? (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </svg>
    ) : (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    )}
  </button>
);

const EmailEditor = ({ currentEmail }: { currentEmail: string }) => {
  const [step, setStep] = React.useState<'view' | 'input' | 'code'>('view');
  const [newEmail, setNewEmail] = React.useState('');
  const [code, setCode] = React.useState('');
  const [copiedEmail, setCopiedEmail] = React.useState(false);
  const { checkAuth } = useAuth();
  const { t } = useTranslation();
  const httpRef = React.useRef(new HttpFactoryService().createHttpService());

  const { mutate: sendCode, isPending: sendPending } = useMutation({
    mutationFn: (email: string) =>
      httpRef.current.post(BACKEND_KEYS.CHANGE_EMAIL, { newEmail: email }),
    onSuccess: () => {
      toast.success(t('settings.emailCodeSent'));
      setStep('code');
    },
    onError: (err: any) => {
      const msg = err.response?.data?.message;
      const detail = Array.isArray(msg) ? msg[0] : msg;
      toast.error(detail || t('settings.emailChangeFailed'));
    }
  });

  const { mutate: confirmCode, isPending: confirmPending } = useMutation({
    mutationFn: (c: string) =>
      httpRef.current.post(BACKEND_KEYS.CONFIRM_EMAIL, { code: c }),
    onSuccess: () => {
      checkAuth();
      toast.success(t('settings.emailUpdated'));
      setStep('view');
      setNewEmail('');
      setCode('');
    },
    onError: (err: any) => {
      const msg = err.response?.data?.message;
      const detail = Array.isArray(msg) ? msg[0] : msg;
      toast.error(detail || t('settings.emailConfirmFailed'));
    }
  });

  const { mutate: resendCode, isPending: resendPending } = useMutation({
    mutationFn: () =>
      httpRef.current.post(BACKEND_KEYS.RESEND_EMAIL_CONFIRMATION, { email: currentEmail }),
    onSuccess: () => toast.success(t('settings.emailCodeResent')),
    onError: () => toast.error(t('settings.emailResendFailed'))
  });

  const cancel = () => {
    setStep('view');
    setNewEmail('');
    setCode('');
  };

  if (step === 'view') {
    return (
      <div className="flex items-center gap-[8px]">
        <p className="font-body text-[13px] text-[#818185]">{t('settings.email')}</p>
        <button
          type="button"
          onClick={() => {
            navigator.clipboard.writeText(currentEmail);
            setCopiedEmail(true);
            setTimeout(() => setCopiedEmail(false), 2000);
          }}
          className="inline-flex items-center gap-[5px] h-[36px] px-[14px] rounded-[32px] bg-[#EFEFF1] dark:bg-[#28282B] text-[#4A4A4C] dark:text-[#A0A0A3] font-body text-[13px] font-semibold hover:bg-[#E5E5ED] dark:hover:bg-[#333336] transition-all"
        >
          {copiedEmail ? t('common.copied') : currentEmail}
        </button>
        <button
          type="button"
          onClick={() => { setNewEmail(''); setStep('input'); }}
          className="text-[#818185] hover:text-base-black dark:hover:text-white transition-colors flex-shrink-0"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
        </button>
      </div>
    );
  }

  if (step === 'input') {
    return (
      <div>
        <p className="font-body text-[13px] text-[#818185] mb-[6px]">{t('settings.newEmail')}</p>
        <div className="flex items-center gap-[6px]">
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder={t('settings.newEmailPlaceholder')}
            className="flex-1 h-[36px] px-[12px] text-[13px] font-body bg-white border border-gray-700 rounded-[18px] focus:outline-none focus:border-[#BFBFC0] focus:shadow-[0_0_0_4px_rgba(29,29,30,0.12)] transition-all"
          />
          <button
            type="button"
            onClick={() => newEmail.trim() && sendCode(newEmail.trim())}
            disabled={sendPending || !newEmail.trim()}
            className={`h-[36px] px-[12px] rounded-[18px] font-body text-[12px] font-medium text-white transition-all ${
              sendPending || !newEmail.trim() ? 'bg-gray-900 opacity-50' : 'bg-[#1D1D1E] hover:bg-[#2E2E30]'
            }`}
          >
            {sendPending ? t('settings.sending') : t('settings.sendCode')}
          </button>
          <button
            type="button"
            onClick={cancel}
            className="h-[36px] px-[12px] rounded-[18px] border border-gray-500 bg-gray-400 font-body text-[12px] font-medium text-base-black hover:bg-[#E0E0E3] transition-all"
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    );
  }

  // step === 'code'
  return (
    <div>
      <p className="font-body text-[13px] text-[#818185] mb-[6px]">
        {t('settings.enterCode', { email: newEmail })}
      </p>
      <div className="flex items-center gap-[6px]">
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000"
          maxLength={6}
          className="w-[120px] h-[36px] px-[12px] text-[13px] font-mono text-center tracking-[4px] bg-white border border-gray-700 rounded-[18px] focus:outline-none focus:border-[#BFBFC0] focus:shadow-[0_0_0_4px_rgba(29,29,30,0.12)] transition-all"
        />
        <button
          type="button"
          onClick={() => code.length === 6 && confirmCode(code)}
          disabled={confirmPending || code.length !== 6}
          className={`h-[36px] px-[12px] rounded-[18px] font-body text-[12px] font-medium text-white transition-all ${
            confirmPending || code.length !== 6 ? 'bg-gray-900 opacity-50' : 'bg-[#1D1D1E] hover:bg-[#2E2E30]'
          }`}
        >
          {confirmPending ? t('settings.confirming') : t('settings.confirm')}
        </button>
        <button
          type="button"
          onClick={() => resendCode()}
          disabled={resendPending}
          className="h-[36px] px-[12px] rounded-[18px] border border-gray-500 bg-gray-400 font-body text-[12px] font-medium text-base-black hover:bg-[#E0E0E3] transition-all"
        >
          {resendPending ? t('settings.sending') : t('settings.resend')}
        </button>
        <button
          type="button"
          onClick={cancel}
          className="text-[#818185] hover:text-base-black dark:hover:text-white transition-colors flex-shrink-0 flex items-center"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>
  );
};

const trashIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const webhookIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const developerIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
  </svg>
);

const DiscordProviderIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="#5865F2">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.947 2.418-2.157 2.418z" />
  </svg>
);

const SlackProviderIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="#E01E5A">
    <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zm10.122 2.521a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zm-1.268 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zm-2.523 10.122a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.268a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
  </svg>
);

const SecuritySection = () => {
  const { t } = useTranslation();
  const { user, checkAuth } = useAuth();

  // TOTP state
  const [mfaStep, setMfaStep] = React.useState<'idle' | 'setup' | 'verify' | 'backup' | 'disabling'>('idle');
  const [qrCode, setQrCode] = React.useState('');
  const [secret, setSecret] = React.useState('');
  const [backupCodes, setBackupCodes] = React.useState<string[]>([]);
  const [code, setCode] = React.useState('');
  const [copiedSecret, setCopiedSecret] = React.useState(false);
  const [copiedBackup, setCopiedBackup] = React.useState(false);

  // Email OTP state
  const [emailStep, setEmailStep] = React.useState<'idle' | 'verify' | 'confirming'>('idle');
  const [emailCode, setEmailCode] = React.useState('');

  // Push MFA state
  const [pushStep, setPushStep] = React.useState<'idle' | 'subscribing' | 'confirming'>('idle');
  const [pushError, setPushError] = React.useState('');

  const { mutate: startSetup, isPending: setupPending } = useMutation({
    mutationFn: () => authService.mfaSetup(),
    onSuccess: (data) => {
      setQrCode(data.qrCode);
      setSecret(data.secret);
      setMfaStep('setup');
    },
    onError: (err: any) => toast.error(err.response?.data?.message || t('settings.mfaSetupFailed'))
  });

  const { mutate: enableMfa, isPending: enablePending } = useMutation({
    mutationFn: (c: string) => authService.mfaEnable(c),
    onSuccess: (data) => {
      setBackupCodes(data.backupCodes);
      setCode('');
      setMfaStep('backup');
      checkAuth();
      toast.success(t('settings.mfaEnabled'));
    },
    onError: (err: any) => toast.error(err.response?.data?.message || t('settings.mfaVerifyFailed'))
  });

  const { mutate: disableMfa, isPending: disablePending } = useMutation({
    mutationFn: (c: string) => authService.mfaDisable(c),
    onSuccess: () => {
      setMfaStep('idle');
      setCode('');
      checkAuth();
      toast.success(t('settings.mfaDisabled'));
    },
    onError: (err: any) => toast.error(err.response?.data?.message || t('settings.mfaVerifyFailed'))
  });

  // Email OTP mutations
  const { mutate: startEmailSetup, isPending: emailSetupPending } = useMutation({
    mutationFn: () => authService.mfaEmailSetup(),
    onSuccess: () => {
      setEmailStep('verify');
      toast.success(t('settings.emailMfaCodeSent'));
    },
    onError: (err: any) => toast.error(err.response?.data?.message || t('settings.mfaSetupFailed'))
  });

  const { mutate: enableEmailMfa, isPending: emailEnablePending } = useMutation({
    mutationFn: (c: string) => authService.mfaEmailEnable(c),
    onSuccess: () => {
      setEmailStep('idle');
      setEmailCode('');
      checkAuth();
      toast.success(t('settings.emailMfaEnabled'));
    },
    onError: (err: any) => toast.error(err.response?.data?.message || t('settings.mfaVerifyFailed'))
  });

  const { mutate: disableEmailMfa, isPending: emailDisablePending } = useMutation({
    mutationFn: () => authService.mfaEmailDisable(),
    onSuccess: () => {
      setEmailStep('idle');
      checkAuth();
      toast.success(t('settings.emailMfaDisabled'));
    },
    onError: (err: any) => toast.error(err.response?.data?.message || t('settings.mfaVerifyFailed'))
  });

  // Push MFA mutations
  const { mutate: enablePushMfa, isPending: pushEnablePending } = useMutation({
    mutationFn: () => authService.mfaPushEnable(),
    onSuccess: () => {
      setPushStep('idle');
      setPushError('');
      checkAuth();
      toast.success(t('settings.pushMfaEnabled'));
    },
    onError: (err: any) => toast.error(err.response?.data?.message || t('settings.mfaSetupFailed'))
  });

  const { mutate: disablePushMfa, isPending: pushDisablePending } = useMutation({
    mutationFn: () => authService.mfaPushDisable(),
    onSuccess: () => {
      setPushStep('idle');
      checkAuth();
      toast.success(t('settings.pushMfaDisabled'));
    },
    onError: (err: any) => toast.error(err.response?.data?.message || t('settings.mfaVerifyFailed'))
  });

  const handleEnablePush = async () => {
    setPushStep('subscribing');
    setPushError('');
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        setPushError(t('settings.pushMfaNotSupported'));
        setPushStep('idle');
        return;
      }

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setPushError(t('settings.pushMfaPermissionDenied'));
        setPushStep('idle');
        return;
      }

      const registration = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;

      const backendUrl = (process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL_DEV || 'http://localhost:4200').replace(/\/api\/v1\/?$/, '');
      let vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      if (!vapidKey) {
        const res = await fetch(`${backendUrl}/api/v1/push/vapid-key`);
        const data = await res.json();
        vapidKey = data.publicKey;
      }
      if (!vapidKey) {
        setPushError('Push notification configuration unavailable.');
        setPushStep('idle');
        return;
      }

      const padding = '='.repeat((4 - (vapidKey.length % 4)) % 4);
      const base64 = (vapidKey + padding).replace(/-/g, '+').replace(/_/g, '/');
      const rawData = window.atob(base64);
      const appServerKey = new Uint8Array(rawData.length);
      for (let i = 0; i < rawData.length; i++) appServerKey[i] = rawData.charCodeAt(i);

      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: appServerKey
        });
      }

      const p256dh = subscription.getKey('p256dh');
      const auth = subscription.getKey('auth');
      if (!p256dh || !auth) throw new Error('Missing push keys');

      const toBase64 = (buf: ArrayBuffer) => {
        const bytes = new Uint8Array(buf);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
        return window.btoa(binary);
      };

      const authToken = localStorage.getItem('standby_token');
      await fetch(`${backendUrl}/api/v1/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({
          endpoint: subscription.endpoint,
          p256dh: toBase64(p256dh),
          auth: toBase64(auth)
        })
      });

      enablePushMfa();
    } catch (err: any) {
      setPushError(err.message || t('settings.mfaSetupFailed'));
      setPushStep('idle');
    }
  };

  const { mutate: logoutAll, isPending: logoutPending } = useMutation({
    mutationFn: () => authService.logoutAll(),
    onSuccess: () => toast.success(t('settings.logoutAllSuccess')),
    onError: () => toast.error(t('settings.logoutAllFailed'))
  });

  const cancelSetup = () => {
    setMfaStep('idle');
    setQrCode('');
    setSecret('');
    setCode('');
    setBackupCodes([]);
  };

  const downloadBackupCodes = () => {
    const text = `Standby Backup Codes\n${'='.repeat(30)}\n\n${backupCodes.map((c, i) => `${i + 1}. ${c}`).join('\n')}\n\nEach code can only be used once.\nStore these codes in a safe place.`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'standby-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const maskedEmail = user?.email
    ? user.email.replace(/^(.)(.*)(@.)(.*)(\..*$)/, (_, a, b, c, d, e) => a + '*'.repeat(Math.min(b.length, 3)) + c + '*'.repeat(Math.min(d.length, 3)) + e)
    : '';

  return (
    <div>
      <h2 className="font-body text-[18px] font-semibold text-base-black dark:text-white mb-[16px]">
        {t('settings.twoFactorAuth')}
      </h2>

      {/* ---- Authenticator App Card ---- */}
      <div className="mb-[12px]">
        <h3 className="font-body text-[15px] font-semibold text-base-black mb-[2px]">
          {t('settings.authenticatorApp')}
        </h3>

        {/* Idle — TOTP disabled */}
        {mfaStep === 'idle' && !user?.mfaEnabled && (
          <div>
            <p className="font-body text-[14px] text-gray-900 mb-[12px]">
              {t('settings.authenticatorDescription')}
            </p>
            <button
              type="button"
              onClick={() => startSetup()}
              disabled={setupPending}
              className={`h-[36px] px-[20px] rounded-[32px] font-body text-[13px] font-medium text-white transition-all ${
                setupPending ? 'bg-gray-900 opacity-50' : 'bg-base-black hover:bg-[#2E2E30]'
              }`}
            >
              {setupPending ? t('common.loading') : t('settings.enableMfa')}
            </button>
          </div>
        )}

        {/* Idle — TOTP enabled */}
        {mfaStep === 'idle' && user?.mfaEnabled && (
          <div>
            <div className="flex items-center gap-[8px] mb-[8px]">
              <span className="inline-flex items-center gap-[4px] text-[12px] font-semibold px-[8px] py-[2px] rounded-[5px] bg-[#ECFDF5] text-[#059669]">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                {t('settings.mfaActive')}
              </span>
            </div>
            <p className="font-body text-[14px] text-gray-900 mb-[12px]">
              {t('settings.mfaEnabledDescription')}
            </p>
            <button
              type="button"
              onClick={() => { setCode(''); setMfaStep('disabling'); }}
              className="h-[36px] px-[20px] rounded-[32px] border border-red-500 text-red-500 font-body text-[13px] font-medium hover:bg-red-500 hover:text-white transition-all"
            >
              {t('settings.disableMfa')}
            </button>
          </div>
        )}

        {/* Setup — show QR code */}
        {mfaStep === 'setup' && (
          <div>
            <p className="font-body text-[14px] text-gray-900 mb-[16px]">
              {t('settings.mfaScanQr')}
            </p>
            <div className="flex flex-col items-center gap-[12px] p-[16px] rounded-[12px] bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)] mb-[16px]">
              {qrCode && <img src={qrCode} alt="MFA QR Code" className="w-[200px] h-[200px]" />}
              <p className="font-body text-[12px] text-[#818185]">{t('settings.mfaCantScan')}</p>
              <button
                type="button"
                onClick={() => { navigator.clipboard.writeText(secret); setCopiedSecret(true); setTimeout(() => setCopiedSecret(false), 2000); }}
                className="inline-flex items-center gap-[5px] h-[32px] px-[12px] rounded-[32px] bg-[#EFEFF1] dark:bg-[#28282B] font-mono text-[12px] text-[#4A4A4C] dark:text-[#A0A0A3] hover:bg-[#E5E5ED] dark:hover:bg-[#333336] dark:hover:text-white transition-all"
              >
                {copiedSecret ? t('common.copied') : secret}
              </button>
            </div>
            <p className="font-body text-[13px] font-medium text-base-black mb-[8px]">
              {t('settings.mfaEnterCode')}
            </p>
            <div className="flex items-center gap-[8px]">
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                autoFocus
                className="w-[120px] h-[44px] px-[16px] text-[16px] font-mono text-center tracking-[4px] bg-white border border-gray-700 rounded-[24px] placeholder-gray-900 focus:outline-none focus:border-[#BFBFC0] focus:shadow-[0_0_0_4px_rgba(29,29,30,0.12)] transition-all"
              />
              <button
                type="button"
                onClick={() => code.length === 6 && enableMfa(code)}
                disabled={enablePending || code.length !== 6}
                className={`h-[44px] px-[20px] rounded-[32px] font-body text-[14px] font-medium transition-all ${
                  enablePending || code.length !== 6 ? 'bg-gray-900 text-white opacity-50' : 'bg-base-black text-white dark:bg-[#C8C8CB] dark:text-[#1C1C1F] hover:bg-[#2E2E30] dark:hover:bg-[#D8D8DB]'
                }`}
              >
                {enablePending ? t('settings.verifying') : t('settings.verify')}
              </button>
              <button
                type="button"
                onClick={cancelSetup}
                className="h-[44px] px-[20px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[14px] font-medium text-base-black dark:text-[#E8E8EA] hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}

        {/* Backup codes */}
        {mfaStep === 'backup' && (
          <div>
            <p className="font-body text-[14px] font-semibold text-base-black mb-[4px]">
              {t('settings.mfaSaveBackupCodes')}
            </p>
            <p className="font-body text-[13px] text-gray-900 mb-[12px]">
              {t('settings.mfaBackupCodesDescription')}
            </p>
            <div className="grid grid-cols-2 gap-[6px] mb-[12px]">
              {backupCodes.map((bc) => (
                <div key={bc} className="h-[36px] flex items-center justify-center font-mono text-[14px] text-base-black bg-[#F5F5F6] border border-[#E5E5ED] rounded-[8px]">
                  {bc}
                </div>
              ))}
            </div>
            <div className="flex items-center gap-[8px]">
              <button
                type="button"
                onClick={() => { navigator.clipboard.writeText(backupCodes.join('\n')); setCopiedBackup(true); setTimeout(() => setCopiedBackup(false), 2000); }}
                className="h-[36px] px-[16px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[13px] font-medium text-base-black dark:text-[#E8E8EA] hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all"
              >
                {copiedBackup ? t('common.copied') : t('settings.copyAll')}
              </button>
              <button
                type="button"
                onClick={downloadBackupCodes}
                className="font-body text-[13px] font-medium text-[#818185] hover:text-base-black transition-colors"
              >
                {t('settings.downloadTxt')}
              </button>
            </div>
            <button
              type="button"
              onClick={cancelSetup}
              className="mt-[16px] h-[44px] px-[24px] rounded-[32px] bg-base-black text-white font-body text-[14px] font-medium hover:bg-[#2E2E30] transition-all"
            >
              {t('settings.done')}
            </button>
          </div>
        )}

        {/* Disabling TOTP */}
        {mfaStep === 'disabling' && (
          <div>
            <p className="font-body text-[14px] text-gray-900 mb-[12px]">
              {t('settings.mfaDisablePrompt')}
            </p>
            <div className="flex items-center gap-[8px]">
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                placeholder="000000"
                maxLength={8}
                autoFocus
                className="w-[140px] h-[44px] px-[16px] text-[16px] font-mono text-center tracking-[4px] bg-white border border-gray-700 rounded-[24px] placeholder-gray-900 focus:outline-none focus:border-[#BFBFC0] focus:shadow-[0_0_0_4px_rgba(29,29,30,0.12)] transition-all"
              />
              <button
                type="button"
                onClick={() => code.length >= 6 && disableMfa(code)}
                disabled={disablePending || code.length < 6}
                className={`h-[44px] px-[20px] rounded-[32px] font-body text-[14px] font-medium text-white transition-all ${
                  disablePending || code.length < 6 ? 'bg-gray-900 opacity-50' : 'bg-red-500 hover:bg-[#B91C0C]'
                }`}
              >
                {disablePending ? t('settings.verifying') : t('settings.confirmDisable')}
              </button>
              <button
                type="button"
                onClick={() => { setMfaStep('idle'); setCode(''); }}
                className="h-[44px] px-[20px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[14px] font-medium text-base-black dark:text-[#E8E8EA] hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Divider between methods */}
      <div className="border-t border-[rgba(0,0,0,0.07)] my-[16px]" />

      {/* ---- Email Verification Card ---- */}
      <div className="mb-[12px]">
        <h3 className="font-body text-[15px] font-semibold text-base-black mb-[2px]">
          {t('settings.emailVerification')}
        </h3>

        {/* Idle — Email OTP disabled */}
        {emailStep === 'idle' && !user?.mfaEmailEnabled && (
          <div>
            <p className="font-body text-[14px] text-gray-900 mb-[12px]">
              {t('settings.emailMfaDescription', { email: maskedEmail })}
            </p>
            <button
              type="button"
              onClick={() => startEmailSetup()}
              disabled={emailSetupPending}
              className={`h-[36px] px-[20px] rounded-[32px] font-body text-[13px] font-medium transition-all ${
                emailSetupPending ? 'bg-gray-900 text-white opacity-50' : 'bg-base-black text-white dark:bg-[#C8C8CB] dark:text-[#1C1C1F] hover:bg-[#2E2E30] dark:hover:bg-[#D8D8DB]'
              }`}
            >
              {emailSetupPending ? t('common.loading') : t('settings.enableEmailMfa')}
            </button>
          </div>
        )}

        {/* Idle — Email OTP enabled */}
        {emailStep === 'idle' && user?.mfaEmailEnabled && (
          <div>
            <div className="flex items-center gap-[8px] mb-[8px]">
              <span className="inline-flex items-center gap-[4px] text-[12px] font-semibold px-[8px] py-[2px] rounded-[5px] bg-[#ECFDF5] text-[#059669]">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                {t('settings.mfaActive')}
              </span>
            </div>
            <p className="font-body text-[14px] text-gray-900 mb-[12px]">
              {t('settings.emailMfaEnabledDescription', { email: maskedEmail })}
            </p>
            <button
              type="button"
              onClick={() => {
                if (!user?.mfaEnabled) {
                  setEmailStep('confirming');
                } else {
                  disableEmailMfa();
                }
              }}
              disabled={emailDisablePending}
              className={`h-[36px] px-[20px] rounded-[32px] border border-red-500 text-red-500 font-body text-[13px] font-medium transition-all ${
                emailDisablePending ? 'opacity-50' : 'hover:bg-red-500 hover:text-white'
              }`}
            >
              {emailDisablePending ? t('common.loading') : t('settings.disableEmailMfa')}
            </button>
          </div>
        )}

        {/* Confirming disable — warn if only method */}
        {emailStep === 'confirming' && (
          <div>
            <p className="font-body text-[14px] text-red-500 mb-[12px]">
              {t('settings.emailMfaDisableWarning')}
            </p>
            <div className="flex items-center gap-[8px]">
              <button
                type="button"
                onClick={() => disableEmailMfa()}
                disabled={emailDisablePending}
                className={`h-[36px] px-[20px] rounded-[32px] font-body text-[13px] font-medium text-white transition-all ${
                  emailDisablePending ? 'bg-gray-900 opacity-50' : 'bg-red-500 hover:bg-[#B91C0C]'
                }`}
              >
                {emailDisablePending ? t('common.loading') : t('settings.confirmDisable')}
              </button>
              <button
                type="button"
                onClick={() => setEmailStep('idle')}
                className="h-[36px] px-[20px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[13px] font-medium text-base-black dark:text-[#E8E8EA] hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}

        {/* Verify — enter code sent to email */}
        {emailStep === 'verify' && (
          <div>
            <p className="font-body text-[14px] text-gray-900 mb-[12px]">
              {t('settings.emailMfaEnterCode', { email: maskedEmail })}
            </p>
            <div className="flex items-center gap-[8px]">
              <input
                type="text"
                value={emailCode}
                onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                autoFocus
                className="w-[120px] h-[44px] px-[16px] text-[16px] font-mono text-center tracking-[4px] bg-white border border-gray-700 rounded-[24px] placeholder-gray-900 focus:outline-none focus:border-[#BFBFC0] focus:shadow-[0_0_0_4px_rgba(29,29,30,0.12)] transition-all"
              />
              <button
                type="button"
                onClick={() => emailCode.length === 6 && enableEmailMfa(emailCode)}
                disabled={emailEnablePending || emailCode.length !== 6}
                className={`h-[44px] px-[20px] rounded-[32px] font-body text-[14px] font-medium transition-all ${
                  emailEnablePending || emailCode.length !== 6 ? 'bg-gray-900 text-white opacity-50' : 'bg-base-black text-white dark:bg-[#C8C8CB] dark:text-[#1C1C1F] hover:bg-[#2E2E30] dark:hover:bg-[#D8D8DB]'
                }`}
              >
                {emailEnablePending ? t('settings.verifying') : t('settings.verify')}
              </button>
              <button
                type="button"
                onClick={() => { setEmailStep('idle'); setEmailCode(''); }}
                className="h-[44px] px-[20px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[14px] font-medium text-base-black dark:text-[#E8E8EA] hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all"
              >
                {t('common.cancel')}
              </button>
            </div>
            <button
              type="button"
              onClick={() => startEmailSetup()}
              disabled={emailSetupPending}
              className="mt-[8px] font-body text-[13px] font-medium text-[#818185] hover:text-base-black transition-colors"
            >
              {emailSetupPending ? t('common.loading') : t('settings.resendEmailCode')}
            </button>
          </div>
        )}
      </div>

      {/* ---- Push Notifications Card ---- */}
      <div className="mt-[20px] mb-[12px]">
        <h3 className="font-body text-[15px] font-semibold text-base-black dark:text-white mb-[2px]">
          {t('settings.pushMfaTitle')}
        </h3>

        {/* Idle — Push disabled */}
        {pushStep === 'idle' && !user?.mfaPushEnabled && (
          <div>
            <p className="font-body text-[14px] text-gray-900 mb-[12px]">
              {t('settings.pushMfaDescription')}
            </p>
            {pushError && (
              <p className="font-body text-[13px] text-red-500 mb-[8px]">{pushError}</p>
            )}
            {!user?.mfaEnabled && !user?.mfaEmailEnabled ? (
              <p className="font-body text-[13px] text-[#818185] italic">
                {t('settings.pushMfaRequiresBackup')}
              </p>
            ) : (
              <button
                type="button"
                onClick={handleEnablePush}
                disabled={pushEnablePending}
                className={`h-[36px] px-[20px] rounded-[32px] font-body text-[13px] font-medium text-white transition-all ${
                  pushEnablePending ? 'bg-gray-900 opacity-50' : 'bg-base-black hover:bg-[#2E2E30]'
                }`}
              >
                {pushEnablePending ? t('common.loading') : t('settings.enablePushMfa')}
              </button>
            )}
          </div>
        )}

        {/* Subscribing state */}
        {pushStep === 'subscribing' && (
          <div>
            <p className="font-body text-[14px] text-gray-900 mb-[12px]">
              {t('settings.pushMfaSubscribing')}
            </p>
            <div className="h-[36px] flex items-center">
              <span className="font-body text-[13px] text-[#818185]">{t('common.loading')}</span>
            </div>
          </div>
        )}

        {/* Idle — Push enabled */}
        {pushStep === 'idle' && user?.mfaPushEnabled && (
          <div>
            <div className="flex items-center gap-[8px] mb-[8px]">
              <span className="inline-flex items-center gap-[4px] text-[12px] font-semibold px-[8px] py-[2px] rounded-[5px] bg-[#ECFDF5] text-[#059669]">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                {t('settings.mfaActive')}
              </span>
            </div>
            <p className="font-body text-[14px] text-gray-900 mb-[12px]">
              {t('settings.pushMfaEnabledDescription')}
            </p>
            <button
              type="button"
              onClick={() => {
                if (!user?.mfaEnabled && !user?.mfaEmailEnabled) {
                  setPushStep('confirming');
                } else {
                  disablePushMfa();
                }
              }}
              disabled={pushDisablePending}
              className={`h-[36px] px-[20px] rounded-[32px] border border-red-500 text-red-500 font-body text-[13px] font-medium transition-all ${
                pushDisablePending ? 'opacity-50' : 'hover:bg-red-500 hover:text-white'
              }`}
            >
              {pushDisablePending ? t('common.loading') : t('settings.disablePushMfa')}
            </button>
          </div>
        )}

        {/* Confirming disable — warn if only method */}
        {pushStep === 'confirming' && (
          <div>
            <p className="font-body text-[14px] text-red-500 mb-[12px]">
              {t('settings.pushMfaDisableWarning')}
            </p>
            <div className="flex items-center gap-[8px]">
              <button
                type="button"
                onClick={() => disablePushMfa()}
                disabled={pushDisablePending}
                className={`h-[36px] px-[20px] rounded-[32px] font-body text-[13px] font-medium text-white transition-all ${
                  pushDisablePending ? 'bg-gray-900 opacity-50' : 'bg-red-500 hover:bg-[#B91C0C]'
                }`}
              >
                {pushDisablePending ? t('common.loading') : t('settings.confirmDisable')}
              </button>
              <button
                type="button"
                onClick={() => setPushStep('idle')}
                className="h-[36px] px-[20px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[13px] font-medium text-base-black dark:text-[#E8E8EA] hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="border-t border-[rgba(0,0,0,0.07)] my-[20px]" />

      {/* Active sessions / logout all */}
      <h2 className="font-body text-[18px] font-semibold text-base-black mb-[4px]">
        {t('settings.activeSessions')}
      </h2>
      <p className="font-body text-[14px] text-gray-900 mb-[16px]">
        {t('settings.logoutAllDescription')}
      </p>
      <button
        type="button"
        onClick={() => logoutAll()}
        disabled={logoutPending}
        className={`h-[44px] px-[24px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[14px] font-medium text-base-black dark:text-[#E8E8EA] transition-all ${
          logoutPending ? 'opacity-50' : 'hover:bg-[#E0E0E3] dark:hover:bg-[#333336]'
        }`}
      >
        {logoutPending ? t('common.loading') : t('settings.logoutAllDevices')}
      </button>
    </div>
  );
};

const SavedWebhooksSection = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [showForm, setShowForm] = React.useState(false);
  const [nameInput, setNameInput] = React.useState('');
  const [urlInput, setUrlInput] = React.useState('');
  const [channelUrlInput, setChannelUrlInput] = React.useState('');
  const [editingId, setEditingId] = React.useState<number | null>(null);
  const [editName, setEditName] = React.useState('');
  const [editUrl, setEditUrl] = React.useState('');
  const [editChannelUrl, setEditChannelUrl] = React.useState('');

  const detectedProvider = urlInput.startsWith('https://discord.com/api/webhooks/')
    ? 'discord'
    : urlInput.startsWith('https://hooks.slack.com/services/')
    ? 'slack'
    : null;

  const { data: webhooks = [] } = useQuery<IUserWebhook[]>({
    queryKey: ['user-webhooks'],
    queryFn: () => authService.getUserWebhooks(),
  });

  const { mutate: createWebhook, isPending: creating } = useMutation({
    mutationFn: (data: { name: string; url: string; provider: string; channelUrl?: string | null }) =>
      authService.createUserWebhook(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-webhooks'] });
      setNameInput('');
      setUrlInput('');
      setChannelUrlInput('');
      setShowForm(false);
    },
    onError: () => toast.error(t('settings.webhookSaveFailed')),
  });

  const { mutate: deleteWebhook } = useMutation({
    mutationFn: (id: number) => authService.deleteUserWebhook(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['user-webhooks'] }),
    onError: () => toast.error(t('settings.webhookDeleteFailed')),
  });

  const { mutate: updateWebhook, isPending: updating } = useMutation({
    mutationFn: ({ id, data }: { id: number; data: { name?: string; url?: string; channelUrl?: string | null } }) =>
      authService.updateUserWebhook(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-webhooks'] });
      setEditingId(null);
      toast.success(t('settings.webhookUpdated'));
    },
    onError: () => toast.error(t('settings.webhookUpdateFailed')),
  });

  const maskUrl = (url: string, provider: string) => {
    if (provider === 'discord') return 'discord.com/api/webhooks/\u2022\u2022\u2022\u2022';
    if (provider === 'slack') return 'hooks.slack.com/services/\u2022\u2022\u2022\u2022';
    return '\u2022\u2022\u2022\u2022';
  };

  return (
    <div>
      <h2 className="font-body text-[18px] font-semibold text-base-black mb-[4px]">
        {t('settings.webhooks')}
      </h2>
      <p className="font-body text-[14px] text-gray-900 mb-[20px]">
        {t('settings.webhooksDescription')}
      </p>

      {webhooks.length === 0 && !showForm && (
        <p className="font-body text-[14px] text-gray-900 mb-[16px]">
          {t('settings.noWebhooks')}
        </p>
      )}
      {webhooks.length > 0 && (
        <div className="flex flex-col gap-[6px] mb-[16px]">
          {webhooks.map((wh) => (
            <div key={wh.id} className="rounded-[12px] border border-[rgba(0,0,0,0.11)] bg-[#F5F5F6] overflow-hidden">
              {editingId === wh.id ? (
                <div className="flex flex-col gap-[8px] p-[12px_16px]">
                  <input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder={t('settings.webhookNamePlaceholder')}
                    className="h-[36px] px-[12px] text-[13px] font-body bg-white border border-gray-700 rounded-[18px] placeholder-gray-900 focus:outline-none focus:border-[#BFBFC0] transition-all"
                  />
                  <input
                    value={editUrl}
                    onChange={(e) => setEditUrl(e.target.value)}
                    placeholder={t('settings.webhookUrlPlaceholder')}
                    className="h-[36px] px-[12px] text-[13px] font-mono bg-white border border-gray-700 rounded-[18px] placeholder-gray-900 focus:outline-none focus:border-[#BFBFC0] transition-all"
                  />
                  {wh.provider === 'slack' && (
                    <input
                      value={editChannelUrl}
                      onChange={(e) => setEditChannelUrl(e.target.value)}
                      placeholder={t('settings.slackChannelUrlPlaceholder')}
                      className="h-[36px] px-[12px] text-[13px] font-body bg-white border border-gray-700 rounded-[18px] placeholder-gray-900 focus:outline-none focus:border-[#BFBFC0] transition-all"
                    />
                  )}
                  <div className="flex gap-[6px]">
                    <button
                      type="button"
                      disabled={!editName.trim() || !editUrl.trim() || updating}
                      onClick={() => updateWebhook({ id: wh.id, data: { name: editName, url: editUrl, channelUrl: wh.provider === 'slack' ? (editChannelUrl.trim() || null) : undefined } })}
                      className="h-[30px] px-[12px] rounded-[18px] bg-base-black text-white font-body text-[12px] font-medium disabled:opacity-50 hover:bg-[#2E2E30] transition-all"
                    >
                      {updating ? t('common.saving') : t('common.save')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingId(null)}
                      className="h-[30px] px-[12px] rounded-[18px] border border-gray-500 bg-gray-400 font-body text-[12px] font-medium text-base-black hover:bg-[#E0E0E3] transition-all"
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-[10px] p-[12px_16px]">
                  {wh.provider === 'discord' ? <DiscordProviderIcon /> : <SlackProviderIcon />}
                  <div className="flex-1 min-w-0">
                    <p className="font-body text-[14px] font-semibold text-base-black truncate">{wh.name}</p>
                    <p className="font-mono text-[11px] text-gray-900">{maskUrl(wh.url, wh.provider)}</p>
                    {wh.provider === 'slack' && wh.channelUrl && (
                      <a href={wh.channelUrl} target="_blank" rel="noopener noreferrer" className="font-body text-[11px] text-[#0ea5c9] hover:underline truncate block max-w-full">
                        {t('review.openChannel')}
                      </a>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => { setEditingId(wh.id); setEditName(wh.name); setEditUrl(wh.url); setEditChannelUrl(wh.channelUrl || ''); }}
                    className="text-[#818185] hover:text-base-black dark:hover:text-white transition-colors flex-shrink-0"
                    title={t('common.edit')}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteWebhook(wh.id)}
                    className="text-[#818185] hover:text-[#D8210C] transition-colors flex-shrink-0"
                    title={t('settings.deleteWebhook')}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showForm ? (
        <div className="flex flex-col gap-[8px] p-[16px] rounded-[12px] border border-[rgba(0,0,0,0.11)] bg-[#F9F9FA]">
          <div className="relative">
            <input
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder={t('settings.webhookUrlPlaceholder')}
              className="w-full h-[44px] px-[16px] pr-[44px] text-[14px] font-body bg-white border border-gray-700 rounded-[24px] placeholder-gray-900 focus:outline-none focus:border-[#BFBFC0] focus:shadow-[0_0_0_4px_rgba(29,29,30,0.12)] transition-all"
            />
            {detectedProvider && (
              <div className="absolute right-[14px] top-1/2 -translate-y-1/2">
                {detectedProvider === 'discord' ? <DiscordProviderIcon /> : <SlackProviderIcon />}
              </div>
            )}
          </div>
          <input
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            placeholder={t('settings.webhookNamePlaceholder')}
            className="h-[44px] px-[16px] text-[14px] font-body bg-white border border-gray-700 rounded-[24px] placeholder-gray-900 focus:outline-none focus:border-[#BFBFC0] focus:shadow-[0_0_0_4px_rgba(29,29,30,0.12)] transition-all"
          />
          {detectedProvider === 'slack' && (
            <input
              value={channelUrlInput}
              onChange={(e) => setChannelUrlInput(e.target.value)}
              placeholder={t('settings.slackChannelUrlPlaceholder')}
              className="h-[44px] px-[16px] text-[14px] font-body bg-white border border-gray-700 rounded-[24px] placeholder-gray-900 focus:outline-none focus:border-[#BFBFC0] focus:shadow-[0_0_0_4px_rgba(29,29,30,0.12)] transition-all"
            />
          )}
          <div className="flex gap-[8px]">
            <button
              type="button"
              disabled={!detectedProvider || !nameInput.trim() || !urlInput.trim() || creating}
              onClick={() => detectedProvider && createWebhook({ name: nameInput, url: urlInput, provider: detectedProvider, channelUrl: (detectedProvider === 'slack' && channelUrlInput.trim()) ? channelUrlInput.trim() : undefined })}
              className="h-[36px] px-[16px] rounded-[32px] bg-base-black text-white dark:bg-[#C8C8CB] dark:text-[#1C1C1F] font-body text-[13px] font-medium hover:bg-[#2E2E30] dark:hover:bg-[#D8D8DB] disabled:opacity-50 transition-all"
            >
              {creating ? t('common.saving') : t('common.save')}
            </button>
            <button
              type="button"
              onClick={() => { setShowForm(false); setUrlInput(''); setNameInput(''); setChannelUrlInput(''); }}
              className="h-[36px] px-[16px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[13px] font-medium text-base-black dark:text-[#E8E8EA] hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all"
            >
              {t('common.cancel')}
            </button>
          </div>
          {urlInput.trim() && !detectedProvider && (
            <p className="font-body text-[12px] text-gray-900 ml-[4px]">{t('settings.webhookUnknownProvider')}</p>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="h-[36px] px-[16px] rounded-[32px] bg-base-black text-white dark:bg-[#C8C8CB] dark:text-[#1C1C1F] font-body text-[13px] font-medium hover:bg-[#2E2E30] dark:hover:bg-[#D8D8DB] transition-all"
        >
          {t('settings.addWebhook')}
        </button>
      )}
    </div>
  );
};

interface ApiKeyInfo {
  id: number;
  prefix: string;
  lastUsedAt: string | null;
  createdAt: string;
}

interface OAuthApp {
  id: number;
  name: string;
  description: string | null;
  clientId: string;
  secretPrefix: string;
  redirectUris: string[];
  createdAt: string;
  _count: { tokens: number };
}

// ─── Shared copy button ───────────────────────────────────────────────────────

const CopyButton = ({ value, label = 'Copy' }: { value: string; label?: string }) => {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      className="shrink-0 h-[28px] px-[10px] flex items-center gap-[5px] border border-[rgba(0,0,0,0.08)] dark:border-[rgba(255,255,255,0.1)] rounded-[6px] font-body text-[12px] text-[#818185] dark:text-[#6B6B6F] hover:text-base-black dark:hover:text-white hover:border-[rgba(0,0,0,0.2)] dark:hover:border-[rgba(255,255,255,0.2)] transition-all"
    >
      {copied ? (
        <><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg> Copied</>
      ) : (
        <><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg> {label}</>
      )}
    </button>
  );
};

// ─── Revealed key banner ──────────────────────────────────────────────────────

const RevealedKeyBanner = ({ keyValue, onDismiss }: { keyValue: string; onDismiss: () => void }) => {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(keyValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="bg-[#ecfdf5] dark:bg-[#166534]/20 border border-[#bbf7d0] dark:border-[#166534]/40 rounded-[12px] p-[14px] flex flex-col gap-[8px]">
      <div className="flex items-center gap-[8px]">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
        <p className="font-body text-[13px] font-semibold text-[#166534] dark:text-[#4ade80]">
          Key generated. Save it now, it will not be shown again.
        </p>
      </div>
      <div className="flex items-center gap-[8px]">
        <code className="flex-1 font-mono text-[11px] bg-white dark:bg-[#1C1C1F] border border-[#bbf7d0] dark:border-[#166534]/40 rounded-[8px] px-[10px] py-[7px] text-base-black dark:text-white break-all">
          {keyValue}
        </code>
        <button
          type="button"
          onClick={handleCopy}
          className="shrink-0 h-[32px] px-[12px] flex items-center gap-[5px] bg-[#166534] text-white rounded-[8px] font-body text-[12px] font-medium hover:bg-[#14532d] transition-all"
        >
          {copied ? (
            <><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg> Copied</>
          ) : (
            <><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg> Copy</>
          )}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 h-[32px] w-[32px] flex items-center justify-center text-[#818185] hover:text-base-black dark:hover:text-white transition-colors"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        </button>
      </div>
    </div>
  );
};


// ─── Developer section ─────────────────────────────────────────────────────────

const ApiKeysSection = () => {
  const queryClient = useQueryClient();
  const [revealedKey, setRevealedKey] = React.useState<string | null>(null);
  const [confirmRegen, setConfirmRegen] = React.useState(false);

  const { data: apiKey, isLoading } = useQuery<ApiKeyInfo | null>({
    queryKey: ['developer-api-key'],
    queryFn: async () => {
      const res = await http.get<ApiKeyInfo>(BACKEND_KEYS.DEVELOPER_API_KEYS);
      return (res as any) ?? null;
    }
  });

  const regenMutation = useMutation({
    mutationFn: async () => {
      const res = await http.post<any, Record<string, never>>(
        BACKEND_KEYS.DEVELOPER_API_KEY_REGENERATE, {}
      );
      return res as any;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['developer-api-key'] });
      setRevealedKey(data?.key ?? null);
      setConfirmRegen(false);
    },
    onError: () => toast.error('Failed to generate API key')
  });

  // ─── OAuth Apps ────────────────────────────────────────────────────

  const [showAppForm, setShowAppForm] = React.useState(false);
  const [appName, setAppName] = React.useState('');
  const [appDesc, setAppDesc] = React.useState('');
  const [redirectUri, setRedirectUri] = React.useState('');
  const [revealedSecret, setRevealedSecret] = React.useState<{ appName: string; secret: string } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = React.useState<number | null>(null);

  const { data: oauthApps = [], isLoading: appsLoading } = useQuery<OAuthApp[]>({
    queryKey: ['oauth-apps'],
    queryFn: async () => {
      const res = await http.get<OAuthApp[]>(BACKEND_KEYS.OAUTH_APPS);
      return (res as any) ?? [];
    }
  });

  const createAppMutation = useMutation({
    mutationFn: async () => {
      const res = await http.post<any, any>(BACKEND_KEYS.OAUTH_APPS, {
        name: appName.trim(),
        description: appDesc.trim() || undefined,
        redirectUris: [redirectUri.trim()]
      });
      return res as any;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['oauth-apps'] });
      setRevealedSecret({ appName: data.name, secret: data.clientSecret });
      setShowAppForm(false);
      setAppName(''); setAppDesc(''); setRedirectUri('');
    },
    onError: () => toast.error('Failed to create OAuth app')
  });

  const deleteAppMutation = useMutation({
    mutationFn: async (id: number) => {
      await http.delete(`${BACKEND_KEYS.OAUTH_APPS}/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['oauth-apps'] });
      setDeleteConfirm(null);
    },
    onError: () => toast.error('Failed to delete OAuth app')
  });

  const resetSecretMutation = useMutation({
    mutationFn: async ({ id, name }: { id: number; name: string }) => {
      const res = await http.post<any, Record<string, never>>(
        `${BACKEND_KEYS.OAUTH_APPS}/${id}/reset-secret`, {}
      );
      return { ...(res as any), name };
    },
    onSuccess: (data) => {
      setRevealedSecret({ appName: data.name, secret: data.clientSecret });
    },
    onError: () => toast.error('Failed to reset secret')
  });

  return (
    <div className="flex flex-col gap-[24px]">
      <div>
        <h2 className="font-body text-[18px] font-semibold text-base-black dark:text-white mb-[4px]">Developer</h2>
        <p className="font-body text-[13px] text-[#818185] dark:text-[#6B6B6F]">
          API keys and OAuth apps for building on top of Standby.
        </p>
      </div>

      {/* ── API Key ── */}
      <div className="flex flex-col gap-[10px]">
        <p className="font-body text-[13px] font-semibold text-base-black dark:text-white">API Key</p>
        <p className="font-body text-[12px] text-[#818185] dark:text-[#6B6B6F] -mt-[4px]">
          One key per account. Keys start with <code className="font-mono text-[11px] bg-[#EFEFF1] dark:bg-[#28282B] px-[4px] py-[0.5px] rounded-[3px]">sk_live_</code> and cannot be retrieved after generation.
        </p>

        {revealedKey && (
          <RevealedKeyBanner keyValue={revealedKey} onDismiss={() => setRevealedKey(null)} />
        )}

        {isLoading ? (
          <p className="font-body text-[12px] text-[#818185]">Loading...</p>
        ) : apiKey ? (
          <div className="border border-[rgba(0,0,0,0.07)] dark:border-[rgba(255,255,255,0.06)] rounded-[10px] px-[14px] py-[12px] flex flex-col sm:flex-row sm:items-center gap-[8px] sm:gap-[12px]">
            <div className="flex-1 min-w-0">
              <p className="font-mono text-[12px] text-base-black dark:text-white">
                {apiKey.prefix}{'•'.repeat(12)}
              </p>
              <p className="font-body text-[11px] text-[#818185] dark:text-[#6B6B6F] mt-[2px]">
                Generated {new Date(apiKey.createdAt).toLocaleDateString()}
                {apiKey.lastUsedAt && ` · Last used ${new Date(apiKey.lastUsedAt).toLocaleDateString()}`}
              </p>
            </div>
            {confirmRegen ? (
              <div className="flex items-center gap-[8px]">
                <p className="font-body text-[12px] text-[#818185] dark:text-[#6B6B6F]">Regenerate? Old key stops working.</p>
                <button
                  type="button"
                  onClick={() => regenMutation.mutate()}
                  disabled={regenMutation.isPending}
                  className="h-[28px] px-[10px] flex items-center bg-[#D8210C] text-white rounded-[6px] font-body text-[12px] font-medium hover:bg-[#b91c1c] disabled:opacity-50 transition-all"
                >
                  {regenMutation.isPending ? '...' : 'Regenerate'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmRegen(false)}
                  className="h-[28px] px-[10px] flex items-center border border-gray-500 bg-gray-400 rounded-[6px] font-body text-[12px] text-base-black hover:bg-[#E0E0E3] transition-all"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmRegen(true)}
                className="shrink-0 h-[28px] px-[10px] flex items-center border border-[rgba(0,0,0,0.08)] dark:border-[rgba(255,255,255,0.1)] rounded-[6px] font-body text-[12px] text-[#818185] dark:text-[#6B6B6F] hover:text-base-black dark:hover:text-white transition-all"
              >
                Regenerate
              </button>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => regenMutation.mutate()}
            disabled={regenMutation.isPending}
            className="self-start h-[38px] px-[18px] flex items-center gap-[7px] bg-base-black text-white rounded-[32px] font-body text-[13px] font-medium hover:bg-[#2E2E30] disabled:opacity-50 transition-all"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" /></svg>
            {regenMutation.isPending ? 'Generating...' : 'Generate API Key'}
          </button>
        )}
      </div>

      {/* ── OAuth Apps ── */}
      <div className="flex flex-col gap-[10px]">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-body text-[13px] font-semibold text-base-black dark:text-white">OAuth Apps</p>
            <p className="font-body text-[12px] text-[#818185] dark:text-[#6B6B6F] mt-[1px]">
              Register apps to use the OAuth 2.0 Authorization Code flow.
            </p>
          </div>
          {!showAppForm && (
            <button
              type="button"
              onClick={() => setShowAppForm(true)}
              className="shrink-0 h-[32px] px-[14px] flex items-center gap-[6px] bg-base-black text-white rounded-[32px] font-body text-[12px] font-medium hover:bg-[#2E2E30] transition-all"
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
              New App
            </button>
          )}
        </div>

        {revealedSecret && (
          <div className="bg-[#ecfdf5] dark:bg-[#166534]/20 border border-[#bbf7d0] dark:border-[#166534]/40 rounded-[12px] p-[14px] flex flex-col gap-[8px]">
            <div className="flex items-center gap-[8px]">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
              <p className="font-body text-[13px] font-semibold text-[#166534] dark:text-[#4ade80]">
                Client secret for <span className="font-bold">{revealedSecret.appName}</span>. Save it now.
              </p>
            </div>
            <div className="flex items-center gap-[8px]">
              <code className="flex-1 font-mono text-[11px] bg-white dark:bg-[#1C1C1F] border border-[#bbf7d0] dark:border-[#166534]/40 rounded-[8px] px-[10px] py-[7px] text-base-black dark:text-white break-all">
                {revealedSecret.secret}
              </code>
              <CopyButton value={revealedSecret.secret} />
              <button type="button" onClick={() => setRevealedSecret(null)} className="shrink-0 h-[32px] w-[32px] flex items-center justify-center text-[#818185] hover:text-base-black dark:hover:text-white transition-colors">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
          </div>
        )}

        {showAppForm && (
          <div className="border border-[rgba(0,0,0,0.08)] dark:border-[rgba(255,255,255,0.06)] rounded-[12px] p-[16px] flex flex-col gap-[10px]">
            <p className="font-body text-[13px] font-semibold text-base-black dark:text-white">Register OAuth App</p>
            <input
              type="text"
              value={appName}
              onChange={(e) => setAppName(e.target.value)}
              placeholder="App name"
              className="h-[38px] rounded-[24px] border border-gray-700 dark:border-[rgba(255,255,255,0.12)] bg-white dark:bg-[#28282B] px-[14px] font-body text-[13px] text-base-black dark:text-white placeholder:text-[#818185] outline-none focus:border-[#BCEBFB] focus:shadow-[0_0_0_3px_rgba(93,225,255,0.15)] transition-all"
            />
            <input
              type="text"
              value={appDesc}
              onChange={(e) => setAppDesc(e.target.value)}
              placeholder="Description (optional)"
              className="h-[38px] rounded-[24px] border border-gray-700 dark:border-[rgba(255,255,255,0.12)] bg-white dark:bg-[#28282B] px-[14px] font-body text-[13px] text-base-black dark:text-white placeholder:text-[#818185] outline-none focus:border-[#BCEBFB] focus:shadow-[0_0_0_3px_rgba(93,225,255,0.15)] transition-all"
            />
            <input
              type="url"
              value={redirectUri}
              onChange={(e) => setRedirectUri(e.target.value)}
              placeholder="Redirect URI (https://...)"
              className="h-[38px] rounded-[24px] border border-gray-700 dark:border-[rgba(255,255,255,0.12)] bg-white dark:bg-[#28282B] px-[14px] font-body text-[13px] text-base-black dark:text-white placeholder:text-[#818185] outline-none focus:border-[#BCEBFB] focus:shadow-[0_0_0_3px_rgba(93,225,255,0.15)] transition-all"
            />
            <div className="flex gap-[8px]">
              <button
                type="button"
                disabled={!appName.trim() || !redirectUri.trim() || createAppMutation.isPending}
                onClick={() => createAppMutation.mutate()}
                className="h-[36px] px-[16px] flex items-center bg-base-black text-white rounded-[32px] font-body text-[12px] font-medium hover:bg-[#2E2E30] disabled:opacity-50 transition-all"
              >
                {createAppMutation.isPending ? 'Creating...' : 'Create App'}
              </button>
              <button
                type="button"
                onClick={() => { setShowAppForm(false); setAppName(''); setAppDesc(''); setRedirectUri(''); }}
                className="h-[36px] px-[14px] flex items-center border border-gray-500 bg-gray-400 rounded-[32px] font-body text-[12px] text-base-black hover:bg-[#E0E0E3] transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {appsLoading ? (
          <p className="font-body text-[12px] text-[#818185]">Loading...</p>
        ) : oauthApps.length === 0 ? (
          <p className="font-body text-[12px] text-[#818185] dark:text-[#6B6B6F]">No OAuth apps registered yet.</p>
        ) : (
          <div className="flex flex-col gap-[6px]">
            {oauthApps.map((app) => (
              <div key={app.id} className="border border-[rgba(0,0,0,0.07)] dark:border-[rgba(255,255,255,0.06)] rounded-[10px] px-[14px] py-[12px] flex flex-col gap-[8px]">
                <div className="flex items-start justify-between gap-[8px]">
                  <div className="min-w-0">
                    <p className="font-body text-[13px] font-semibold text-base-black dark:text-white">{app.name}</p>
                    {app.description && (
                      <p className="font-body text-[12px] text-[#818185] dark:text-[#6B6B6F]">{app.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-[6px] shrink-0">
                    <button
                      type="button"
                      onClick={() => resetSecretMutation.mutate({ id: app.id, name: app.name })}
                      disabled={resetSecretMutation.isPending}
                      className="h-[26px] px-[8px] flex items-center border border-[rgba(0,0,0,0.08)] dark:border-[rgba(255,255,255,0.1)] rounded-[6px] font-body text-[11px] text-[#818185] dark:text-[#6B6B6F] hover:text-base-black dark:hover:text-white transition-all disabled:opacity-50"
                    >
                      Reset secret
                    </button>
                    {deleteConfirm === app.id ? (
                      <>
                        <button
                          type="button"
                          onClick={() => deleteAppMutation.mutate(app.id)}
                          disabled={deleteAppMutation.isPending}
                          className="h-[26px] px-[8px] flex items-center bg-[#D8210C] text-white rounded-[6px] font-body text-[11px] font-medium hover:bg-[#b91c1c] disabled:opacity-50 transition-all"
                        >
                          Delete
                        </button>
                        <button type="button" onClick={() => setDeleteConfirm(null)} className="h-[26px] px-[8px] flex items-center border border-gray-500 bg-gray-400 rounded-[6px] font-body text-[11px] text-base-black hover:bg-[#E0E0E3] transition-all">Cancel</button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setDeleteConfirm(app.id)}
                        className="h-[26px] px-[8px] flex items-center border border-[rgba(0,0,0,0.08)] dark:border-[rgba(255,255,255,0.1)] rounded-[6px] font-body text-[11px] text-[#818185] dark:text-[#6B6B6F] hover:border-[#D8210C] hover:text-[#D8210C] dark:hover:text-[#f87171] transition-all"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-[4px]">
                  <div className="flex items-center gap-[6px]">
                    <span className="font-body text-[11px] text-[#818185] dark:text-[#6B6B6F] w-[72px] shrink-0">Client ID</span>
                    <code className="font-mono text-[11px] text-base-black dark:text-white flex-1 truncate">{app.clientId}</code>
                    <CopyButton value={app.clientId} />
                  </div>
                  <div className="flex items-center gap-[6px]">
                    <span className="font-body text-[11px] text-[#818185] dark:text-[#6B6B6F] w-[72px] shrink-0">Secret</span>
                    <code className="font-mono text-[11px] text-[#818185] dark:text-[#6B6B6F] flex-1">{app.secretPrefix}{'•'.repeat(8)}</code>
                  </div>
                  <div className="flex items-center gap-[6px]">
                    <span className="font-body text-[11px] text-[#818185] dark:text-[#6B6B6F] w-[72px] shrink-0">Redirect</span>
                    <code className="font-mono text-[11px] text-[#818185] dark:text-[#6B6B6F] flex-1 truncate">{app.redirectUris[0]}</code>
                  </div>
                </div>
                <p className="font-body text-[11px] text-[#818185] dark:text-[#6B6B6F]">
                  {app._count.tokens} active token{app._count.tokens !== 1 ? 's' : ''} · Created {new Date(app.createdAt).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
